import type { AnchorHTMLAttributes, ElementType, HTMLAttributes, ReactNode } from "react";
import { cn } from "@/lib/utils";

export type ReferenceKind = "mention" | "link" | "evidence";
export type ReferenceTargetKind = "entity" | "location" | "version" | "diff";

type ReferenceChipBaseProps = {
  as?: ElementType;
  label?: ReactNode;
  children?: ReactNode;
  icon?: ElementType | null;
  interactive?: boolean;
  referenceKind?: ReferenceKind;
  targetKind?: ReferenceTargetKind | null;
  iconKey?: string | null;
  className?: string;
};

export type ReferenceChipProps = ReferenceChipBaseProps &
  Omit<HTMLAttributes<HTMLElement>, keyof ReferenceChipBaseProps> &
  Omit<AnchorHTMLAttributes<HTMLAnchorElement>, keyof ReferenceChipBaseProps>;

export function normalizeReferenceLabel(label: string): string {
  const trimmed = label.trim();
  return trimmed.startsWith("@") ? trimmed.slice(1).trimStart() : trimmed;
}

function isTextLeaf(value: ReactNode): value is string | number {
  return typeof value === "string" || typeof value === "number";
}

function normalizeTextOnlyReferenceContent(value: ReactNode): ReactNode {
  if (isTextLeaf(value)) {
    return normalizeReferenceLabel(String(value));
  }
  if (Array.isArray(value) && value.every(isTextLeaf)) {
    return normalizeReferenceLabel(value.map(String).join(""));
  }
  return value;
}

export function ReferenceChip({
  as,
  label,
  children,
  icon: Icon,
  interactive = false,
  referenceKind,
  targetKind,
  iconKey,
  className,
  ...props
}: ReferenceChipProps) {
  const Component = as ?? "span";
  const content = normalizeTextOnlyReferenceContent(label ?? children);

  return (
    <Component
      {...props}
      className={cn(
        "reference-chip",
        referenceKind && `reference-chip--${referenceKind}`,
        referenceKind === "mention" && "mention",
        interactive && "reference-chip--interactive",
        interactive && referenceKind === "mention" && "mention--interactive",
        className
      )}
      data-reference-kind={referenceKind}
      data-reference-target-kind={targetKind ?? undefined}
      data-reference-icon-key={iconKey ?? undefined}
    >
      {Icon ? (
        <span className="reference-chip__icon" aria-hidden="true">
          <Icon className="size-3" aria-hidden="true" />
        </span>
      ) : null}
      <span className="reference-chip__label">{content}</span>
    </Component>
  );
}
